import React, { useContext, useEffect } from 'react';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';

import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Drawer from '@material-ui/core/Drawer';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Container from '@material-ui/core/Container';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import Paper from '@material-ui/core/Paper';
import Backdrop from '@material-ui/core/Backdrop';

import Footer from 'components/Footer';
import ErrorBoundary from 'components/ErrorBoundary';
import { KeycloakContext } from 'components/Secured';
import WelcomeDialog from 'components/WelcomeDialog';
import useLayoutContext from 'hooks/useLayoutContext';
import useViewport from 'hooks/useViewports';
import { QUERY_INIT_PRODUCER_CHECK } from 'graphql/queries';
import { extractURLParam } from 'utils/urlUtils';

import AuthMenu from './AuthMenu';
import HelpMenu from './HelpMenu';
import { localAuthEmulation } from 'utils/localAuthEmulationGate';

const drawerWidth = 240;

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        width: '100%',
        transition: 'width 0.3s ease-in-out',
    },
    toolbar: {
        paddingRight: 24, // keep right padding when drawer closed
    },
    toolbarIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 8px',
        ...theme.mixins.toolbar,
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        backgroundColor: '#56aba9',
    },
    appBarShift: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    menuButton: {
        marginRight: 36,
    },
    menuButtonHidden: {
        display: 'none',
    },
    title: {
        flexGrow: 1,
    },
    drawerPaper: {
        position: 'relative',
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    drawerPaperClose: {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
            width: theme.spacing(9),
        },
    },
    appBarSpacer: theme.mixins.toolbar,
    content: {
        flexGrow: 1,
        overflow: 'auto',
        height: '100vh',
    },
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    assistantContentWrap: {
        minWidth: 0,
        minHeight: 0,
    },
    assistantContent: {
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '@supports (height: 100dvh)': {
            height: '100dvh',
        },
    },
    assistantContainer: {
        minHeight: 0,
        flex: '1 1 auto',
        display: 'flex',
        paddingTop: 0,
        paddingBottom: 0,
        maxWidth: 'none',
    },
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
    },
    fixedHeight: {
        height: 240,
    },
    contentWrap: {
        position: 'relative',
        flexGrow: 1,     // sits to the right of the Drawer
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
    },
}));

const Layout = (props) => {
    const { ready: layoutReady, mdUp } = useViewport();
    const { setCheckingProducer, rightbarWidth } = useLayoutContext();
    const classes = useStyles();
    const [open, setOpen] = React.useState(true);
    const location = useLocation();
    const rawPathname = location?.pathname?.substring(1);
    const isAssistant = rawPathname === 'assistant';
    const isAiBulkReview = rawPathname === 'bulk-import-new/review' || rawPathname === 'superuser/bulk-import-new/review';

    const keycloakCtx = useContext(KeycloakContext);
    const isStore = keycloakCtx.keycloak.hasResourceRole('store');
    const isAdmin = keycloakCtx.keycloak.hasResourceRole('admin');
    const isSuperuser = keycloakCtx.keycloak.hasResourceRole('superuser');

    // eslint-disable-next-line no-unused-vars
    const { data: producerInitData, loading: loadingProducerInit, error: errorProducerInit } = useQuery(QUERY_INIT_PRODUCER_CHECK, {
      fetchPolicy: 'cache-and-network',
    });

    let title = () => {
        const v = rawPathname;
        const isNonFood = extractURLParam('nonfood', window.location) === "1";
        if (!v || v === '') {
            return 'Dashboard';
        } else if (v === 'assistant') {
            return 'Produktassistent';
        } else if (v.startsWith('producers/data')) {
            return 'Data';
        } else if (v.startsWith('me/products')) {
            return 'My Products';
        } else if ((isStore || isSuperuser) && v.startsWith('products')) {
            return 'All Products';
        } else if (v.startsWith('superuser/products')) {
            return 'Product Approval';
        } else if (v.startsWith('superuser/producers')) {
            return 'Producers Approval';
        } else if (v.startsWith('superuser/organizations')) {
            return 'Organizations';
        } else if (v.startsWith('superuser/export-history')) {
          return 'Export History';
        } else if (v.startsWith('superuser/bulk-import')) {
            return 'Bulkimport';
        } else if (v.startsWith('superuser/export')) {
            return isNonFood ? 'Export Non Food' : 'Export Products';
        } else if (v.startsWith('superuser/failed')) {
            return 'Failed Products';
        } else if (isSuperuser && (v === 'producers' || v.startsWith('producers?'))) {
            return 'All Producers';
        } else if (v.startsWith('superuser/producer/') && v.endsWith('/edit')) {
            return 'Edit Producer';
        } else if (v.startsWith('superuser/organization/') && v.endsWith('/edit')) {
            return 'Edit Organization';
        } else if (v.startsWith('me/') && v.endsWith('/edit')) {
            return 'Edit';
        } else if (v.startsWith('products/') && v.endsWith('/edit')) {
            return 'Edit Product';
        } else if (v.startsWith('producers/') && v.endsWith('/show')) {
            return 'Producer';
        } else if (v.startsWith('products/') && v.endsWith('/show')) {
            return 'Produkter';
        } else if (v.startsWith('producers/') && v.endsWith('/products')) {
            return 'Produkter';
        } else if (v.startsWith('bulk-import')) {
            return 'Bulkimport';
        } else if (v.startsWith('superuser/bulk-review')) {
            return 'Bulk Review';
        } else if (v.startsWith('superuser/settings/ai')) {
            return 'AI settings';
        } else if (v.startsWith('superuser/statistics')) {
            return 'Statistics';
        } else if (v.startsWith('notification/')) {
            return 'Notification';
        } else if (v.endsWith('/addproduct')) {
            return 'Add Product';
        } else if (v.startsWith('order/')) {
            return 'Orders';
        } else if (v === 'products') {
            return 'Produkter';
        } else if (v === 'me') {
            return 'Min profil';
        } else {
            return v[0].toUpperCase() + v.slice(1);
        }

        // const values = location?.pathname?.substring(1)?.split('/');
        // const value = values && values[0];
        // if(!value || value === '') {
        //     return 'Dashboard';
        // } else if (value === 'myproducts') {
        //     return 'My Products';
        // } else {
        //     return value[0].toUpperCase() + value.slice(1);
        // }
    };

    const handleDrawerOpen = () => {
        setOpen(true);
    };
    const handleDrawerClose = () => {
        setOpen(false);
    };

    useEffect(() => {
      if (layoutReady) {
        setOpen(mdUp);
      }
    }, [layoutReady, mdUp]);

    let containerMaxWidth = rawPathname.endsWith('bulk-import') ? "xl" : "lg";
    if (isAssistant) containerMaxWidth = false;
    if (isAiBulkReview) containerMaxWidth = false;
    const pathList = rawPathname.split('/');
    if (pathList[1] === 'bulk-review' && pathList[2] && pathList[3]) {
      containerMaxWidth = false;
    }
    // The AI statistics: wide tables of imports and reviews.
    if (rawPathname.startsWith('superuser/statistics') && !rawPathname.startsWith('superuser/statistics/coop-ga')) containerMaxWidth = "xl";

    useEffect(() => {
      setCheckingProducer(loadingProducerInit);
    }, [loadingProducerInit, setCheckingProducer]);

    const groups = keycloakCtx?.keycloak?.idTokenParsed?.groups?.filter((x) => x.includes('/coop/'));
    const isCoop = groups && groups?.length > 0;

    const blur = isCoop && (isAdmin || isStore);

    return (
        <div className={classes.root} style={{ width: `calc(100% - ${rightbarWidth}px)` }}>
            <CssBaseline />
            <WelcomeDialog />
            <AppBar
                position="absolute"
                className={clsx(classes.appBar, open && classes.appBarShift)}
            >
                <Toolbar className={classes.toolbar}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="Open drawer"
                        onClick={handleDrawerOpen}
                        className={clsx(
                            classes.menuButton,
                            open && classes.menuButtonHidden
                        )}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography
                        component="h1"
                        variant="h6"
                        color="inherit"
                        noWrap
                        className={classes.title}
                    >
                        {title()}
                    </Typography>
                    {process.env.NODE_ENV === 'development' && localAuthEmulation && <localAuthEmulation.Switcher />}
                    <HelpMenu />
                    <AuthMenu />
                </Toolbar>
            </AppBar>
            <Drawer
                variant={mdUp ? "permanent" : undefined}
                classes={{
                    paper: clsx(
                        classes.drawerPaper,
                        !open && classes.drawerPaperClose
                    ),
                }}
                open={open}
                onClose={handleDrawerClose}
            >
                <div className={classes.toolbarIcon}>
                    <IconButton onClick={handleDrawerClose}>
                        <ChevronLeftIcon />
                    </IconButton>
                </div>
                <Divider />
                <List onClick={() => !mdUp && setOpen(false)}>
                    {React.createElement(props.menu)}
                </List>
            </Drawer>

            <div className={clsx(classes.contentWrap, isAssistant && classes.assistantContentWrap)} style={{ minWidth: isAiBulkReview ? 0 : undefined }}>
                {blur && (
                    <>
                <Backdrop 
                    open 
                    disablePortal
                    style={{
                        position: 'absolute',  // cover only this wrapper
                        inset: 0,              // top:0,right:0,bottom:0,left:0
                        zIndex: 2,
                        backdropFilter: 'blur(6px)',
                        background: 'rgba(0,0,0,0.2)',
                    }} 
                />
                <Paper
                    elevation={6}
                    style={{
                        position: 'absolute',
                        zIndex: 2,
                        top: '20vh',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        maxWidth: 700,
                        padding: 24,
                    }}
                >
                    <h2>Export av artiklar till coop.se pausas 1 okt 2025</h2>
                    <p>
                        Sygrids centrala avtal med Coop löper ut den 1 oktober 2025. Tills nytt avtal är på plats är Sygrids tjänster mot Coop tillfälligt avstängda och ingen export av artiklar sker till Coop. Vi för dialog om en fortsättning och uppdaterar er så snart vi vet mer.
                    </p>
                    <p>
                        Vi vill göra det enkelt och kostnadseffektivt för butiker och era leverantörer att lägga upp och hantera produkter för visning online. Vill ni fortsätta använda Sygrid mot Coop? Hjälp oss visa behovet genom att mejla <a href="mailto:support@sygrid.com" target="_blank" rel="noreferrer">support@sygrid.com</a> (ange butik, ort och kontaktperson).
                    </p>
                </Paper>
                </>)}
                <main id="page-main" className={clsx(classes.content, isAssistant && classes.assistantContent)}>
                    <div className={classes.appBarSpacer} />
                    <Container
                    className={clsx(classes.container, isAssistant && classes.assistantContainer)}
                    maxWidth={containerMaxWidth}
                    disableGutters={isAssistant}
                    >
                        <ErrorBoundary>
                            {props.children}
                        </ErrorBoundary>
                    </Container>
                    {!isAssistant && <Footer />}
                </main>
            </div>
        </div>
    );
}
export default Layout;
